import { cn } from '@/ui/utils'
import { BlurView } from 'expo-blur'
import { Image } from 'expo-image'
import type { ImageSource } from 'expo-image'
import { type ReactNode, useState } from 'react'
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native'
import Animated from 'react-native-reanimated'
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg'

/**
 * The website's way of building a page: not one ground with cards on it, but a rhythm of
 * surfaces — a pink section, then a black one, then white, then a photograph. `Block` is one of
 * those surfaces. It bleeds to the screen's edges inside PageShell's column so the colour goes
 * wall to wall, which is what makes it read as a section rather than a card.
 */
type Tone = 'ground' | 'white' | 'ink' | 'mint' | 'accent' | 'blush'

const TONE: Record<Tone, string> = {
	ground: 'bg-ground',
	white: 'bg-white',
	ink: 'bg-ink',
	mint: 'bg-mint',
	accent: 'bg-accent',
	blush: 'bg-blush',
}

export const Block = ({
	tone = 'white',
	bleed = true,
	className,
	children,
}: {
	tone?: Tone
	/** Run to the screen's edges; off for a block that sits inside another. */
	bleed?: boolean
	className?: string
	children: ReactNode
}) => (
	<View
		className={cn(
			TONE[tone],
			bleed && '-mx-5 lg:-mx-14',
			'px-5 py-6 lg:px-14',
			className
		)}
	>
		{children}
	</View>
)

/**
 * A photograph as a surface, with white type over it. The scrim is what keeps the type legible
 * on a bright smile; it is a flat darkening rather than a gradient, because the website's are.
 */
export const PhotoBlock = ({
	source,
	height = 320,
	scrim = 0.28,
	bleed = true,
	className,
	children,
}: {
	source: ImageSource
	height?: number
	/** 0–1, how much the photo is darkened under the type. */
	scrim?: number
	bleed?: boolean
	className?: string
	children?: ReactNode
}) => (
	<View
		className={cn('overflow-hidden justify-end', bleed && '-mx-5 lg:-mx-14', className)}
		style={{ height }}
	>
		<Image
			source={source}
			style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
			contentFit="cover"
			transition={300}
		/>
		<View
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: `rgba(0,0,0,${scrim})`,
			}}
		/>
		<View className="px-5 pb-6 lg:px-14">{children}</View>
	</View>
)

/**
 * A dark fade up from the bottom edge, so white type reads on any photograph. The canvas is
 * measured rather than given percentages, which react-native-svg cannot resolve inside a
 * flex-sized box. `to` is how dark the bottom edge gets; a form on the photo wants more.
 */
export const PhotoFade = ({
	from = 0.05,
	to = 0.55,
	start = 0.3,
}: { from?: number; to?: number; start?: number } = {}) => {
	const [size, setSize] = useState({ width: 0, height: 0 })

	return (
		<View
			pointerEvents="none"
			style={StyleSheet.absoluteFill}
			onLayout={(e) => setSize(e.nativeEvent.layout)}
		>
			{size.height > 0 && (
				<Svg width={size.width} height={size.height}>
					<Defs>
						<LinearGradient id="photo-fade" x1="0" y1="0" x2="0" y2="1">
							<Stop offset={start} stopColor="#000" stopOpacity={from} />
							<Stop offset="1" stopColor="#000" stopOpacity={to} />
						</LinearGradient>
					</Defs>
					<Rect width={size.width} height={size.height} fill="url(#photo-fade)" />
				</Svg>
			)}
		</View>
	)
}

/**
 * The glass edge on a panel that scrolls inside a card.
 *
 * A scroll clipped by a card cuts its rows in half at the top and the foot, and a half row reads
 * as a rendering fault rather than as "there is more". Under glass the same row is still there,
 * blurred and washed with the card's own colour, which says the list carries on past the edge.
 * It is the treatment the navigation already uses over the page scrolling beneath it.
 *
 * It is only there when something is actually past the edge: glass over a list that fits is a
 * blur laid across a first line nobody has scrolled away. It starts hidden for the same reason —
 * left to the animation's default it would come up opaque and fade out, which is a flash of blur
 * over the first row every time a step is drawn.
 *
 * Kept to the card's own edge. Deep enough to soften the cut and no deeper: a band that reaches a
 * whole row puts a row behind frosted glass, which is not the same as saying the list carries on.
 */
export const EdgeGlass = ({
	colour,
	edge,
	height = 14,
	style,
}: {
	colour: string
	edge: 'top' | 'bottom'
	height?: number
	style?: StyleProp<ViewStyle>
}) => (
	<Animated.View
		pointerEvents="none"
		style={[
			{ position: 'absolute', left: 0, right: 0, height, opacity: 0, [edge]: 0 },
			style,
		]}
	>
		<BlurView intensity={18} tint="light" style={StyleSheet.absoluteFill} />
		<View style={[StyleSheet.absoluteFill, { backgroundColor: colour, opacity: 0.6 }]} />
	</Animated.View>
)
